/* Studio schedules (docs/scheduling-routine-automation-proposal.md §2–§3).
 *
 * A schedule is a canvas trigger with type "routine". It runs on a clock (daily, weekly, monthly or
 * hourly), finds the entries that match a native filter as of the run day, and runs its action once
 * per entry, or sends one digest to fixed people. The server re-reads the template on every run, so
 * only the schedule itself decides when the module's routine wakes up.
 *
 * The condition is the filter only, and native only (ruling 2026-09-26): no condition or query
 * script, no script / formula / NOT clauses, no "is me" (a schedule runs with nobody signed in).
 * Everything here mirrors CanvasRoutineTemplate.Validate and CanvasRoutineFilter on the server, so
 * the designer can say what is wrong before a round trip; the server stays the authority.
 */
import moment from "moment";

import { formatSchedule } from "../../commons/formatSchedule";

export const SCHEDULE_TYPE = "routine";
export const DEFAULT_TIME_OF_DAY = "08:00";
export const MAX_PER_RUN = 500;
export const MAX_PER_RUN_CEILING = 2000;
export const CATCH_UP_DAYS = 3;

export const RECURRING_TYPES = Object.freeze([
  { id: "day", name: "Daily" },
  { id: "week", name: "Weekly" },
  { id: "month", name: "Monthly" },
  { id: "hour", name: "Hourly" },
]);

export const WEEKDAYS = Object.freeze([
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" },
]);

export const RELATIVE_DURATIONS = Object.freeze(["day", "week", "month", "year"]);

/* Date operators a schedule gives a relative payload { duration, offset } (FilterProcessor
   processDateFilter: `on` needs one; before/after take one in place of a fixed date). */
export const RELATIVE_OPERATORS = Object.freeze(["on", "gte", "gt", "lte", "lt"]);
const WINDOW_OPERATORS = Object.freeze(["within", "in", "past"]);

/* Filter clause kinds a schedule refuses: script and formula clauses can run the Python engine,
   and the server does not accept NOT groups on a schedule. */
const REFUSED_CLAUSE_TYPES = Object.freeze(["custom", "formula", "not"]);
const GROUP_TYPES = Object.freeze(["and", "or", "not"]);

/* RoutineRun / RunResult / CanvasEntryResult enums, serialised as numbers by the Web API. */
export const RUN_STATUS = Object.freeze(["Running", "Succeeded", "Partly failed", "Failed", "Skipped", "Preview"]);
export const RUN_TRIGGERED_BY = Object.freeze(["Schedule", "Manual", "Event"]);
export const ENTRY_OUTCOME = Object.freeze(["Delivered", "Would deliver", "Already delivered", "No recipient", "Failed"]);

const DAY_FORMAT = "ddd D MMM";

export function isSchedule(template) {
  return !!template && template.type === SCHEDULE_TYPE;
}

export function defaultSchedule() {
  return {
    recurringType: "day",
    every: 1,
    timeOfDay: DEFAULT_TIME_OF_DAY,
    recurringWeeks: [],
    recurringMonthStart: false,
    recurringMonthEnd: false,
    recurringMonthDay: false,
    recurringMonthDayEvery: 0,
  };
}

export function defaultExecution() {
  return {
    mode: "perEntry",
    oncePerEntry: true,
    skipIfEmpty: true,
    catchUp: true,
    maxPerRun: MAX_PER_RUN,
  };
}

export function defaultDigest() {
  return { channels: ["fetias"], recipients: [], subject: "", message: "" };
}

/** A new, unsaved schedule as the module card's Schedule adder opens it. */
export function newScheduleTemplate(actionType = "notification") {
  return {
    item: null,
    type: SCHEDULE_TYPE,
    name: "",
    enabled: true,
    actionType,
    schedule: defaultSchedule(),
    filterSchema: { items: [] },
    execution: defaultExecution(),
  };
}

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Fill in what an older or converted template lacks, never overwriting a value that is present.
 * `set` lets a component pass `this.$set` so added keys are reactive.
 */
export function normalizeTemplate(template, set = (target, key, value) => { target[key] = value; }) {
  if (!isPlainObject(template)) return template;

  const fill = (key, defaults) => {
    if (!isPlainObject(template[key])) {
      set(template, key, defaults);
      return;
    }
    Object.keys(defaults).forEach((k) => {
      if (template[key][k] === undefined) set(template[key], k, defaults[k]);
    });
  };

  fill("schedule", defaultSchedule());
  fill("execution", defaultExecution());
  if (!isPlainObject(template.filterSchema)) set(template, "filterSchema", { items: [] });
  else if (!Array.isArray(template.filterSchema.items)) set(template.filterSchema, "items", []);
  if (template.item === undefined) set(template, "item", null);
  if (isDigest(template)) fill("digest", defaultDigest());
  return template;
}

/**
 * The schedule an administrator converted this timing automation to (`convertedTo`), when it is still
 * in the module; null otherwise.
 */
export function replacementOf(template, triggers) {
  if (!isPlainObject(template) || isSchedule(template) || !template.convertedTo) return null;
  return (triggers || []).find((x) => isSchedule(x) && x.id === template.convertedTo) || null;
}

/**
 * The automation a converted schedule replaces (`convertedFrom`), when it is still in the module; null
 * otherwise.
 */
export function originalOf(schedule, triggers) {
  if (!isSchedule(schedule) || !schedule.convertedFrom) return null;
  return (triggers || []).find((x) => !isSchedule(x) && x && x.id === schedule.convertedFrom) || null;
}

/**
 * Mirror of CanvasRoutineTemplate.OriginalStillOn (ruling 2026-09-26): the original automation keeps
 * working as normal until someone switches it off, converted or not, so its replacement schedule waits
 * while it is on and takes over from its next run once it is switched off or removed.
 */
export function waitsForOriginal(schedule, triggers) {
  const original = originalOf(schedule, triggers);
  return !!original && original.enabled !== false;
}

export function isDigest(template) {
  return !!(template && isPlainObject(template.execution) && template.execution.mode === "digest");
}

const isGroup = (unit) => isPlainObject(unit) && GROUP_TYPES.includes(unit.type) && Array.isArray(unit.items);

/** Leaf conditions, counted through AND / OR groups. */
export function conditionCount(filterSchema) {
  const walk = (items) => (items || []).reduce((n, unit) => n + (isGroup(unit) ? walk(unit.items) : (unit ? 1 : 0)), 0);
  return isPlainObject(filterSchema) ? walk(filterSchema.items) : 0;
}

export function isRelativePayload(payload) {
  return isPlainObject(payload)
    && typeof payload.duration === "string"
    && RELATIVE_DURATIONS.includes(payload.duration)
    && typeof payload.offset === "number"
    && isFinite(payload.offset);
}

const unitOf = (duration) => ({ day: "days", week: "weeks", month: "months", year: "years" }[duration]);

/**
 * The calendar days an `on` clause picks for a run on `runDay` — FilterProcessor.OnDaySpan. Day and
 * week offsets are exact; month and year go through calendar arithmetic that clamps at month end,
 * so a run day can pick several days (28 Feb + 1 month picks 28–31 Mar) or none (31 Jan + 1 month).
 * Returns [first, last] as moments, or null when no day maps back to the run day.
 */
export function resolveOnSpan(payload, runDay) {
  if (!isRelativePayload(payload)) return null;
  const base = moment(runDay).startOf("day");
  const n = Math.trunc(payload.offset);

  if (payload.duration === "day" || payload.duration === "week") {
    const day = base.clone().add(payload.duration === "day" ? n : n * 7, "days");
    return [day, day.clone()];
  }

  const unit = unitOf(payload.duration);
  const centre = base.clone().add(n, unit);
  let first = null;
  let last = null;
  for (let i = -4; i <= 4; i++) {
    const probe = centre.clone().add(i, "days");
    if (probe.clone().subtract(n, unit).isSame(base, "day")) {
      first = first || probe;
      last = probe;
    }
  }
  return first ? [first, last] : null;
}

export function formatSpan(span) {
  if (!span) return "no matching day on this run";
  const [first, last] = span;
  if (first.isSame(last, "day")) return first.format(DAY_FORMAT);
  return first.isSame(last, "month")
    ? `${first.format("D")}–${last.format("D MMM")}`
    : `${first.format("D MMM")}–${last.format("D MMM")}`;
}

/* The window `offset` units from the run day, as FilterProcessor.RelativeUnit / StartAndEndOf cut
   it. The server takes the week's first day from its culture; the label only names the week. */
function unitWindow(payload, runDay) {
  const base = moment(runDay).startOf("day");
  const n = Math.trunc(payload.offset);
  switch (payload.duration) {
    case "day":
      return { label: base.clone().add(n, "days").format(DAY_FORMAT) };
    case "week":
      return { label: `the week of ${base.clone().startOf("week").add(n, "weeks").format("D MMM")}` };
    case "month":
      return { label: base.clone().startOf("month").add(n, "months").format("MMMM YYYY") };
    case "year":
      return { label: base.clone().startOf("year").add(n, "years").format("YYYY") };
  }
  return { label: "" };
}

/**
 * The day (or unit) a relative date clause points at for a run on `runDay`: "Tue 30 Sep",
 * "28–31 Mar", "the week of 29 Sep", "October 2026".
 */
export function relativeTarget(operator, payload, runDay) {
  if (!isRelativePayload(payload)) return "";
  return operator === "on" ? formatSpan(resolveOnSpan(payload, runDay)) : unitWindow(payload, runDay).label;
}

/**
 * What a relative date clause means for a run on `runDay`, as a sentence reads it:
 * `on` → "= Tue 30 Sep"; `lte` → "on or before Tue 30 Sep"; and so on.
 */
export function describeRelative(operator, payload, runDay) {
  if (!isRelativePayload(payload)) return "";
  if (operator === "on") return `= ${formatSpan(resolveOnSpan(payload, runDay))}`;

  const { label } = unitWindow(payload, runDay);
  switch (operator) {
    case "gte": return `on or after ${label}`;
    case "gt": return `after ${label}`;
    case "lte": return `on or before ${label}`;
    case "lt": return `before ${label}`;
  }
  return "";
}

/** "the run day", "3 days after the run day", "1 month before the run day". */
export function offsetPhrase(payload) {
  if (!isRelativePayload(payload)) return "";
  const n = Math.trunc(payload.offset);
  if (n === 0) return "the run day";
  const count = Math.abs(n);
  const unit = count === 1 ? payload.duration : `${payload.duration}s`;
  return `${count} ${unit} ${n > 0 ? "after" : "before"} the run day`;
}

/* Operators that carry no value of their own. */
const VALUELESS_OPERATORS = Object.freeze(["isNone", "none", "isNotNone", "yes", "no"]);
const isBlank = (value) => value === undefined || value === null || value === "";

function unitLabel(unit) {
  return unit.fieldName || unit.fieldId || unit.field || "a field";
}

/**
 * Condition problems, in the server's words where it has them (CanvasRoutineFilter.Validate).
 * `labelOf(unit)` names a clause's field for the message.
 */
export function filterErrors(filterSchema, labelOf = unitLabel) {
  const errors = [];
  if (!isPlainObject(filterSchema)) return errors;
  if (filterSchema.script) errors.push("Filter scripts are not supported on schedules.");

  const walk = (items) => (items || []).forEach((unit) => {
    if (!isPlainObject(unit)) {
      errors.push("A condition is empty.");
      return;
    }
    if (unit.script) {
      errors.push("Script conditions are not supported on schedules; use a field condition.");
      return;
    }
    if (REFUSED_CLAUSE_TYPES.includes(unit.type)) {
      errors.push(unit.type === "not"
        ? "NOT groups are not supported on schedules; use \"is not\" or \"does not contain\" on the field."
        : "Script and formula conditions are not supported on schedules; use a field condition.");
      return;
    }
    if (isGroup(unit)) {
      if (!unit.items.length) errors.push(`An ${unit.type.toUpperCase()} group has no conditions.`);
      walk(unit.items);
      return;
    }

    const label = labelOf(unit);
    if (!unit.fieldId && !unit.field) {
      errors.push("A condition names no field.");
      return;
    }
    if (!unit.operator) {
      errors.push(`The condition on ${label} has no operator.`);
      return;
    }
    if (unit.operator === "me") {
      errors.push(`The condition on ${label} uses "is me", but a schedule runs with no signed-in person.`);
      return;
    }
    if (unit.operator === "on" || WINDOW_OPERATORS.includes(unit.operator)) {
      if (!isRelativePayload(unit.payload)) errors.push(`The condition on ${label} needs a number of days, weeks, months or years.`);
      return;
    }
    if (unit.fieldId === "state") {
      if (!(isPlainObject(unit.payload) && unit.payload.id)) errors.push("Pick the state the condition looks for.");
      return;
    }
    if (!VALUELESS_OPERATORS.includes(unit.operator) && !Array.isArray(unit.items) && isBlank(unit.payload)) {
      errors.push(`The condition on ${label} has no value.`);
    }
  });

  walk(filterSchema.items);
  return errors;
}

/** Relative date clauses; two or more make "once per entry" key on the first (R-4 warning). */
export function relativeDateClauses(filterSchema) {
  const found = [];
  const walk = (items) => (items || []).forEach((unit) => {
    if (isGroup(unit)) walk(unit.items);
    else if (isPlainObject(unit) && (unit.operator === "on" || WINDOW_OPERATORS.includes(unit.operator)
      || (RELATIVE_OPERATORS.includes(unit.operator) && isRelativePayload(unit.payload)))) found.push(unit);
  });
  if (isPlainObject(filterSchema)) walk(filterSchema.items);
  return found;
}

const TIME_OF_DAY = /^([01]?\d|2[0-3]):[0-5]\d$/;

/**
 * Everything that keeps the template from running, checked before Save (CanvasRoutineTemplate.Validate
 * plus the two day-picking rules the legacy designer enforces, SS05969). Returns messages.
 */
export function validateTemplate(template, labelOf) {
  if (!isPlainObject(template)) return ["The schedule is empty."];
  const errors = [];

  const schedule = isPlainObject(template.schedule) ? template.schedule : {};
  const type = String(schedule.recurringType || "day").toLowerCase();
  if (!RECURRING_TYPES.some((x) => x.id === type)) {
    errors.push(`Unsupported schedule '${type}'. Use daily, weekly, monthly or hourly.`);
  } else {
    const every = schedule.every == null || schedule.every === "" ? 1 : Number(schedule.every);
    if (!(every >= 1)) errors.push("'Every' must be 1 or more.");
    if (type === "hour" && every > 23) errors.push("An hourly schedule repeats every 1 to 23 hours.");
    if (schedule.timeOfDay && !TIME_OF_DAY.test(schedule.timeOfDay)) errors.push(`'${schedule.timeOfDay}' is not a time of day (HH:mm).`);
    if (type === "week" && !(Array.isArray(schedule.recurringWeeks) && schedule.recurringWeeks.length)) {
      errors.push("Pick at least one day of the week for the schedule to run on.");
    }
    if (type === "month") {
      const dayOption = schedule.recurringMonthDay && schedule.recurringMonthDayEvery;
      if (!schedule.recurringMonthStart && !schedule.recurringMonthEnd && !dayOption) {
        errors.push("Pick which day of the month the schedule runs on.");
      } else if (schedule.recurringMonthDay && !(schedule.recurringMonthDayEvery >= 1 && schedule.recurringMonthDayEvery <= 31)) {
        errors.push("The day of the month must be between 1 and 31.");
      }
    }
  }

  if (template.conditionScript && String(template.conditionScript).trim()) {
    errors.push("Condition scripts are not supported on schedules. Express the condition with the filter.");
  }
  if (template.queryScript && String(template.queryScript).trim()) {
    errors.push("Query scripts are not supported on schedules. Express the condition with the filter.");
  }

  errors.push(...filterErrors(template.filterSchema, labelOf));

  if (isDigest(template)) {
    const digest = isPlainObject(template.digest) ? template.digest : {};
    if (!(Array.isArray(digest.channels) && digest.channels.length)) errors.push("Choose at least one channel for the digest.");
    if (!(Array.isArray(digest.recipients) && digest.recipients.length)) errors.push("Choose at least one person to receive the digest.");
  } else {
    if (conditionCount(template.filterSchema) === 0) {
      errors.push("Add at least one condition. Without one, the action would run on every entry in the module on every run.");
    }
    const actionType = template.actionType;
    if (!["notification", "automation", "advanced"].includes(actionType)) {
      errors.push("Choose what the schedule does: a notification or an automation.");
    } else if (actionType === "automation" && !isPlainObject(template.automation)) {
      errors.push("The automation has no blocks.");
    } else if (actionType === "advanced" && !(template.afterScript && String(template.afterScript).trim())) {
      errors.push("The advanced action has no script.");
    }
  }

  return errors;
}

/** Tidy the template right before it is written into the canvas. Mutates and returns it. */
export function prepareForSave(template) {
  if (!isPlainObject(template)) return template;
  template.type = SCHEDULE_TYPE;
  template.item = null;
  if (typeof template.name === "string") template.name = template.name.trim();

  // Keys only the legacy (per-entry trigger) designer uses; a schedule never reads them.
  ["trigger", "repeat", "timeModifier", "general", "condition", "conditionState", "startsOn", "onceOn"]
    .forEach((key) => { if (key in template) delete template[key]; });
  ["conditionScript", "queryScript"].forEach((key) => {
    if (key in template && !(template[key] && String(template[key]).trim())) delete template[key];
  });

  const schedule = template.schedule;
  if (isPlainObject(schedule)) {
    const every = parseInt(schedule.every, 10);
    schedule.every = every >= 1 ? every : 1;
    if (schedule.recurringType !== "week") schedule.recurringWeeks = [];
  }

  const execution = template.execution;
  if (isPlainObject(execution)) {
    const max = parseInt(execution.maxPerRun, 10);
    execution.maxPerRun = max >= 1 ? Math.min(max, MAX_PER_RUN_CEILING) : MAX_PER_RUN;
  }
  return template;
}

/** "Every day at 8:00 AM", with the zone the schedule runs in when known. */
export function describeSchedule(schedule, timeZone) {
  if (!isPlainObject(schedule)) return "";
  const type = String(schedule.recurringType || "day").toLowerCase();
  const text = formatSchedule({ ...schedule, recurringType: type, timeZone: null });
  const minute = type === "hour" && schedule.timeOfDay && TIME_OF_DAY.test(schedule.timeOfDay)
    ? ` at :${schedule.timeOfDay.split(":")[1]}`
    : "";
  return text + minute + (timeZone ? ` · ${timeZone}` : "");
}

export function thenPhrase(template) {
  if (isDigest(template)) {
    const people = (template.digest && template.digest.recipients || []).length;
    return `one digest to ${people} ${people === 1 ? "person" : "people"}`;
  }
  switch (template && template.actionType) {
    case "automation": return "run the automation on each entry";
    case "advanced": return "run the script on each entry";
    default: return "notify for each entry";
  }
}

/** The card sentence: "Every day at 8:00 AM · 2 conditions · notify for each entry". */
export function describeTemplate(template, timeZone) {
  if (!isPlainObject(template)) return "";
  const count = conditionCount(template.filterSchema);
  return [
    describeSchedule(template.schedule, timeZone),
    count ? `${count} ${count === 1 ? "condition" : "conditions"}` : "no conditions",
    thenPhrase(template),
  ].filter(Boolean).join(" · ");
}

export function runStatusLabel(status) {
  return RUN_STATUS[status] || (typeof status === "string" ? status : "Unknown");
}

/** "12 matched · 11 delivered · 1 no recipient" — every run row says what it did (§2 copy rules). */
export function runSummary(run) {
  if (!run) return "";
  const parts = [`${run.matchedCount || 0} matched`];
  if (run.deliveredCount) parts.push(`${run.deliveredCount} delivered`);
  if (run.wouldDeliverCount) parts.push(`${run.wouldDeliverCount} would be delivered`);
  if (run.alreadyDeliveredCount) parts.push(`${run.alreadyDeliveredCount} already delivered`);
  if (run.noRecipientCount) parts.push(`${run.noRecipientCount} no recipient`);
  if (run.failedCount) parts.push(`${run.failedCount} failed`);
  if (parts.length === 1 && run.summary) parts.push(run.summary);
  return parts.join(" · ");
}

export function formatDuration(ms) {
  if (!(ms >= 0)) return "";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export function formatRunTime(value) {
  return value ? moment(value).format("ddd D MMM, h:mm A") : "";
}

/** Studio's side of the server (API/CanvasRoutineController.cs). */
export function scheduleApi(client) {
  return {
    status: (vectorId) => client.get("/api/canvasroutine/status", { params: { vectorId } }),
    validate: (vectorId, template) => client.post("/api/canvasroutine/validate", { vectorId, template }),
    preview: (vectorId, template, asOf) => client.post("/api/canvasroutine/preview", { vectorId, template, asOf }),
    run: (vectorId, templateId) => client.post("/api/canvasroutine/run", { vectorId, templateId }),
    runs: (vectorId, templateId) => client.get("/api/canvasroutine/runs", { params: { vectorId, templateId } }),
  };
}

export default {
  SCHEDULE_TYPE,
  isSchedule,
  newScheduleTemplate,
  normalizeTemplate,
  isDigest,
  conditionCount,
  isRelativePayload,
  resolveOnSpan,
  describeRelative,
  offsetPhrase,
  filterErrors,
  relativeDateClauses,
  validateTemplate,
  prepareForSave,
  describeSchedule,
  describeTemplate,
  runStatusLabel,
  runSummary,
  scheduleApi,
};
